// A stored Hyperlink value as a link that opens in a new tab. A value that is not a safe http or https link
// shows as plain text instead. A click never reaches the row or cell behind it, so following the link does
// not also open an editor.
import React from 'react';
import Styled from 'styled-components';
import {Link, Text} from '@forecast-it/design-system';
import {hyperlinkHref} from '../forecast-app/shared/util/customFieldValueValidation';

// The design-system Link is an inline anchor, and its ellipsis styles (overflow, text-overflow, nowrap) do
// nothing on an inline element. Making it a bounded block lets a long link end in an ellipsis on one line.
const OneLineLink = Styled(Link)`
	display: block;
	max-width: 100%;
	min-width: 0;
`;

type CustomFieldHyperlinkProps = {
	value: string | null | undefined;
	// Shown instead of the URL, for example beside an input that already shows it.
	label?: string;
	'data-cy'?: string;
};

export const CustomFieldHyperlink = ({
	value,
	label,
	'data-cy': dataCy = 'custom-field-hyperlink',
}: CustomFieldHyperlinkProps): React.JSX.Element | null => {
	if (!value) return null;
	const href = hyperlinkHref(value);
	if (!href) return <Text>{value}</Text>;
	return (
		<OneLineLink
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			ellipsis
			data-cy={dataCy}
			onClick={(e: React.MouseEvent) => e.stopPropagation()}
		>
			{label ?? href}
		</OneLineLink>
	);
};

export default CustomFieldHyperlink;
